import { KEY_EVENT_REF_TYPES } from "../constants";

// Normalizes each entityType's very different timelineData shape into one tree the Key Events
// "attach to" picker and grouped list can walk generically:
//   { selfLabel, groups: [ { id, name, refType, children: [ {id, name, refType, children:[...]} ] } ] }
// Depth/allowed refTypes per level mirror the backend's KeyEventService.AllowedLinkTypes exactly:
//   portfolio -> Program/Project (2 levels)
//   program   -> Project/Scope/Task (3 levels)
//   projectTimeline -> Scope/Task (2 levels, Scope is top-level since Project owns it directly)
export function buildKeyEventTree(entityType, timelineData = [], directProjects = []) {
  if (entityType === "portfolio") {
    const programGroups = (timelineData || []).map((program) => ({
      id: program.id,
      name: program.name,
      refType: KEY_EVENT_REF_TYPES.PROGRAM,
      children: (program.projects || []).map((project) => ({
        id: project.id,
        name: project.name,
        refType: KEY_EVENT_REF_TYPES.PROJECT,
        children: [],
      })),
    }));

    const directProjectGroups = (directProjects || []).map((project) => ({
      id: project.id,
      name: project.name,
      refType: KEY_EVENT_REF_TYPES.PROJECT,
      children: [],
    }));

    return { selfLabel: "Portfolio (Global)", groups: [...programGroups, ...directProjectGroups] };
  }

  if (entityType === "program") {
    const projectGroups = (timelineData || []).map((project) => ({
      id: project.id,
      name: project.name,
      refType: KEY_EVENT_REF_TYPES.PROJECT,
      children: buildScopeChildren(project.scopeItems || project.scopes || []),
    }));

    return { selfLabel: "Program (Global)", groups: projectGroups };
  }

  if (entityType === "projectTimeline") {
    // timelineData is the single synthetic wrapper row - its scopeItems are the real top level here.
    const scopes = timelineData?.[0]?.scopeItems || [];
    return { selfLabel: "Project (Global)", groups: buildScopeChildren(scopes) };
  }

  return { selfLabel: "Global", groups: [] };
}

function buildScopeChildren(scopes) {
  return (scopes || []).map((scope) => {
    const groupedTasks = (scope.taskGroups || []).flatMap((g) => g.tasks || []);
    const tasks = [...(scope.tasks || []), ...groupedTasks];

    return {
      id: scope.id,
      name: scope.name,
      refType: KEY_EVENT_REF_TYPES.SCOPE,
      children: tasks.map((task) => ({
        id: task.id,
        name: task.name,
        refType: KEY_EVENT_REF_TYPES.TASK,
        children: [],
      })),
    };
  });
}

// Depth-first lookup by id anywhere in the tree, returning {id, name, refType, children} or null.
export function findNodeInTree(tree, id) {
  const walk = (nodes) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = walk(node.children || []);
      if (found) return found;
    }
    return null;
  };
  return walk(tree.groups || []);
}

// Ids of every ancestor group above the given node (nearest-last), for expanding a picker tree
// down to a quick-add target.
export function findAncestorIds(tree, id) {
  const path = [];
  const walk = (nodes) => {
    for (const node of nodes) {
      if (node.id === id) return true;
      path.push(node.id);
      if (walk(node.children || [])) return true;
      path.pop();
    }
    return false;
  };
  walk(tree.groups || []);
  return path;
}
